import {
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { NgOptimizedImage } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DomSanitizer } from '@angular/platform-browser';
import { fromEvent } from 'rxjs';
import { filter } from 'rxjs/operators';
import { ButtonModule } from '@openng/optimus-ui/button';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { bootstrapXLg } from '@ng-icons/bootstrap-icons';
import { environment } from '../../envs/environment';
import { Auth } from '../../services/auth';
import { ToastService } from '../../services/toast.service';
import {
  InboundChatMessage,
  InboundChatMessageType,
  OutboundChatMessage,
  OutboundChatMessageType,
} from '../../models/chat-message.model';

@Component({
  selector: 'app-floating-chat',
  imports: [ButtonModule, NgIcon, NgOptimizedImage],
  viewProviders: [provideIcons({ bootstrapXLg })],
  template: `
    @if (safeUrl()) {
      <div class="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-3">
        @if (isOpen()) {
          <div
            class="w-96 h-[600px] rounded-xl shadow-2xl overflow-hidden border border-surface-200 bg-surface-0">
            <iframe
              #chatFrame
              [src]="safeUrl()"
              class="w-full h-full border-0"
              title="Chat assistant"
              allow="microphone; clipboard-write">
            </iframe>
          </div>
        }
        <p-button
          [rounded]="true"
          [raised]="true"
          size="large"
          [ariaLabel]="isOpen() ? 'Close chat' : 'Chat with Clarita'"
          (onClick)="toggleChat()">
          @if (isOpen()) {
            <ng-icon name="bootstrapXLg" />
          } @else {
            <span class="font-medium">Chat with Clarita</span>
            <img ngSrc="favicon.ico" width="24" height="24" alt="" />
          }
        </p-button>
      </div>
    }
  `,
})
export class FloatingChatComponent {
  private readonly sanitizer = inject(DomSanitizer);
  // With `skipAuth` MSAL is never set up (placeholder config), so don't instantiate `Auth` at all.
  private readonly auth = environment.skipAuth ? null : inject(Auth);
  private readonly toast = inject(ToastService);
  private readonly destroyRef = inject(DestroyRef);

  /** Context forwarded to the chat as a PAYLOAD message. Its shape is intentionally unknown. */
  readonly payload = input<unknown>();

  private readonly chatFrame = viewChild<ElementRef<HTMLIFrameElement>>('chatFrame');
  private readonly isReady = signal(false);
  protected readonly isOpen = signal(false);

  protected readonly safeUrl = computed(() =>
    environment.chatUrl ? this.sanitizer.bypassSecurityTrustResourceUrl(environment.chatUrl) : null
  );

  private readonly chatOrigin = computed(() => {
    if (!environment.chatUrl) return null;
    try {
      return new URL(environment.chatUrl).origin;
    } catch {
      return null;
    }
  });

  constructor() {
    // Listen for inbound messages from the iframe
    fromEvent<MessageEvent>(window, 'message')
      .pipe(
        filter(e => e.origin === this.chatOrigin()),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(e => this.handleInbound(e.data as InboundChatMessage));

    // Re-send TOKEN whenever the token signal changes and chat is ready. Only the token is
    // tracked: the initial context is sent on READY, so becoming ready must not re-trigger this.
    effect(() => {
      const token = this.auth?.token();
      if (token) {
        untracked(() =>
          this.postIfReady({ type: OutboundChatMessageType.TOKEN, payload: { token } })
        );
      }
    });

    // Re-send PAYLOAD whenever it changes and chat is ready (same reasoning as above)
    effect(() => {
      const payload = this.payload();
      if (payload != null) {
        untracked(() => this.postIfReady({ type: OutboundChatMessageType.PAYLOAD, payload }));
      }
    });
  }

  protected toggleChat(): void {
    const opening = !this.isOpen();
    this.isOpen.set(opening);
    if (!opening) {
      this.isReady.set(false);
    }
  }

  private handleInbound(msg: InboundChatMessage): void {
    switch (msg.type) {
      case InboundChatMessageType.READY:
        this.isReady.set(true);
        void this.sendInitialContext();
        break;
      case InboundChatMessageType.TOAST:
        this.toast.showMessage(msg.payload.severity, msg.payload.summary, msg.payload.detail);
        break;
    }
  }

  private async sendInitialContext(): Promise<void> {
    const token = (await this.auth?.getAccessToken()) ?? '';
    this.post({ type: OutboundChatMessageType.TOKEN, payload: { token } });
    const payload = this.payload();
    if (payload != null) {
      this.post({ type: OutboundChatMessageType.PAYLOAD, payload });
    }
  }

  private postIfReady(msg: OutboundChatMessage): void {
    if (this.isReady()) {
      this.post(msg);
    }
  }

  private post(msg: OutboundChatMessage): void {
    const frame = this.chatFrame()?.nativeElement;
    const origin = this.chatOrigin();
    if (frame?.contentWindow && origin) {
      frame.contentWindow.postMessage(msg, origin);
    }
  }
}
